import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, RefreshCw, Share2 } from "lucide-react";
import {
  DailyStoryCanvas,
  type StoryData,
  type StoryVariant,
  type StoryMatch,
  type StoryDaySection,
  type StoryTipper,
  type StoryClub,
} from "./DailyStoryCanvas";

const VARIANTS: { id: StoryVariant; label: string }[] = [
  { id: "nextMatchday", label: "⚽ Nächster Spieltag" },
  { id: "bestTipper", label: "🏆 Bester Tipper" },
  { id: "topVereine", label: "👥 Top Vereine" },
];

// All matchday logic is anchored to Austrian wall-clock time, independent of
// the admin's device timezone.
const TZ = "Europe/Vienna";
const MAX_GAMES = 6; // a two-day matchday can hold more than the old 4

// Vienna-local calendar parts for an ISO timestamp.
function viennaParts(iso: string): { year: number; month: number; day: number; hour: number } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  let hour = parseInt(get("hour"), 10);
  if (hour === 24) hour = 0; // some runtimes report midnight as "24"
  return { year: +get("year"), month: +get("month"), day: +get("day"), hour };
}

const pad = (n: number) => String(n).padStart(2, "0");

// Vienna calendar day of the game itself, e.g. "2026-07-19".
function viennaDateKey(iso: string): string {
  const { year, month, day } = viennaParts(iso);
  return `${year}-${pad(month)}-${pad(day)}`;
}

// Matchday window key: a matchday runs 12:00 → 12:00 (next day) Vienna time.
// Games before 12:00 belong to the previous day's matchday window. Returns the
// Vienna date of the window's first day (day 1).
function matchdayWindowKey(iso: string): string {
  const { year, month, day, hour } = viennaParts(iso);
  if (hour >= 12) return `${year}-${pad(month)}-${pad(day)}`;
  // Before noon → previous calendar day. Use UTC math on the plain Y-M-D so
  // month/year rollovers are handled without DST surprises.
  const prev = new Date(Date.UTC(year, month - 1, day));
  prev.setUTCDate(prev.getUTCDate() - 1);
  return `${prev.getUTCFullYear()}-${pad(prev.getUTCMonth() + 1)}-${pad(prev.getUTCDate())}`;
}

// Long German date for a section heading, e.g. "Sonntag, 19. Juli 2026".
function dateLongDe(iso: string): string {
  return new Date(iso).toLocaleDateString("de-AT", {
    timeZone: TZ,
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

// Minimal shapes of the (loosely-typed) API payloads we actually read here.
type RankRow = {
  rank?: number;
  username?: string;
  club?: string | null;
  name?: string;
  memberCount?: number;
  totalPoints?: number;
};

type GameRow = {
  id: string;
  kickoffTime: string;
  isFinished?: boolean;
  homeTeam: string;
  homeFlag: string;
  awayTeam: string;
  awayFlag: string;
};

const errMessage = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function StoryGeneratorPanel() {
  const [variant, setVariant] = useState<StoryVariant>("nextMatchday");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<StoryData | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [global, groupRanks, allGames] = await Promise.all([
        api.rankings.global() as Promise<RankRow[]>,
        api.rankings.groups() as Promise<RankRow[]>,
        api.games.list() as Promise<GameRow[]>,
      ]);

      // --- Bester Tipper (top 4 global) ---
      const tippers: StoryTipper[] = (global ?? []).slice(0, 4).map((r, i) => ({
        rank: Number(r.rank ?? i + 1),
        username: r.username ?? "",
        club: r.club ?? null,
        points: Number(r.totalPoints ?? 0),
      }));

      // --- Top Vereine (top 4 groups) ---
      const clubs: StoryClub[] = (groupRanks ?? []).slice(0, 4).map((r, i) => ({
        rank: Number(r.rank ?? i + 1),
        name: r.name ?? "",
        members: Number(r.memberCount ?? 0),
        points: Number(r.totalPoints ?? 0),
      }));

      // --- Nächster Spieltag ---
      // A matchday is the 12:00 → 12:00 (next day, Vienna time) window. We pick
      // the window of the earliest game that hasn't finished yet and keep
      // showing it until every game in that window is over. The window is then
      // split into its real calendar days (day 1: 12:00–24:00, day 2: 00:00–12:00),
      // each rendered under its own dated heading.
      const sortByKickoff = (a: GameRow, b: GameRow) =>
        new Date(a.kickoffTime).getTime() - new Date(b.kickoffTime).getTime();

      const unfinished = (allGames ?? [])
        .filter((g) => !g.isFinished)
        .sort(sortByKickoff);

      let days: StoryDaySection[] = [];
      if (unfinished.length > 0) {
        const windowKey = matchdayWindowKey(unfinished[0].kickoffTime);
        const windowGames = (allGames ?? [])
          .filter((g) => matchdayWindowKey(g.kickoffTime) === windowKey)
          .sort(sortByKickoff)
          .slice(0, MAX_GAMES);

        // Tip distribution per game → community favourite (leading side).
        // Uses the aggregated summary endpoint — bounded payload + 10s
        // server cache, instead of downloading every tip row.
        const dists = await Promise.all(
          windowGames.map((g) =>
            api.tips.summary(g.id).catch(() => ({ total: 0, home: 0, draw: 0, away: 0, dist: [], myTip: null })),
          ),
        );

        // Group games by their real Vienna calendar day, preserving order.
        const byDay = new Map<string, { iso: string; matches: StoryMatch[] }>();
        windowGames.forEach((g, idx) => {
          const s = dists[idx];
          const { home, away, total } = s;
          const homeFav = home >= away;
          const favCount = homeFav ? home : away;
          const match: StoryMatch = {
            home_team: g.homeTeam,
            home_flag: g.homeFlag,
            away_team: g.awayTeam,
            away_flag: g.awayFlag,
            time: new Date(g.kickoffTime).toLocaleTimeString("de-AT", {
              timeZone: TZ,
              hour: "2-digit",
              minute: "2-digit",
            }),
            fav_flag: homeFav ? g.homeFlag : g.awayFlag,
            fav_pct: total > 0 ? Math.round((favCount / total) * 100) : 0,
            has_tips: total > 0,
          };
          const key = viennaDateKey(g.kickoffTime);
          const bucket = byDay.get(key) ?? { iso: g.kickoffTime, matches: [] };
          bucket.matches.push(match);
          byDay.set(key, bucket);
        });

        days = [...byDay.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([, bucket]) => ({ dateLong: dateLongDe(bucket.iso), matches: bucket.matches }));
      }

      // Redbox date — generation date, e.g. "Di, 09. Juni 2026"
      const now = new Date();
      const wd = now.toLocaleDateString("de-AT", { timeZone: TZ, weekday: "short" }).replace(".", "");
      const rest = now.toLocaleDateString("de-AT", { timeZone: TZ, day: "2-digit", month: "long", year: "numeric" });
      const dateShort = `${wd}, ${rest}`;

      setData({ variant, dateShort, days, tippers, clubs });
    } catch (e) {
      toast.error(`Daten laden fehlgeschlagen: ${errMessage(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (data) setData({ ...data, variant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const exportPng = async (): Promise<Blob | null> => {
    if (!canvasRef.current) return null;
    setBusy(true);
    try {
      // Fonts must be ready before capture, otherwise html-to-image bakes
      // the system fallback into the PNG.
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      const dataUrl = await toPng(canvasRef.current, {
        width: 1080,
        height: 1920,
        pixelRatio: 1,
        cacheBust: true,
      });
      const res = await fetch(dataUrl);
      return await res.blob();
    } catch (e) {
      toast.error(`Export fehlgeschlagen: ${errMessage(e)}`);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    const blob = await exportPng();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `englputzeder-story-${variant}-${new Date().toISOString().slice(0, 10)}.png`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Story heruntergeladen 📱");
  };

  const share = async () => {
    const blob = await exportPng();
    if (!blob) return;
    const file = new File([blob], `story-${variant}.png`, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Englputzeder Tippspiel" });
      } catch {/* user cancelled */}
    } else {
      download();
      toast.info("Direktes Teilen nicht verfügbar — PNG wurde heruntergeladen.");
    }
  };

  return (
    <section>
      <h2 className="font-display font-bold text-lg mb-2">📱 Daily Instagram Story</h2>
      <div className="bg-card rounded-lg shadow-card p-4 space-y-4">
        <p className="text-xs text-muted-foreground">
          Tagesaktuelle Story (1080×1920) auf Knopfdruck — direkt in IG/WhatsApp teilen oder als PNG laden.
        </p>

        <div className="flex flex-wrap gap-2">
          {VARIANTS.map(v => (
            <button
              key={v.id}
              onClick={() => setVariant(v.id)}
              className={`text-xs font-bold px-3 py-1.5 rounded-pill ${
                variant === v.id ? "bg-accent text-accent-foreground" : "bg-muted"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* Preview (scaled 0.25 → 270×480 box) */}
        <div className="flex justify-center bg-muted/30 rounded-md p-3 overflow-hidden">
          {loading || !data ? (
            <div className="h-[480px] w-[270px] animate-pulse bg-muted rounded" />
          ) : (
            <div
              style={{
                width: 270,
                height: 480,
                overflow: "hidden",
                position: "relative",
                borderRadius: 12,
                boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
              }}
            >
              <div style={{ transform: "scale(0.25)", transformOrigin: "top left" }}>
                <DailyStoryCanvas ref={canvasRef} data={data} />
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Button onClick={load} disabled={loading || busy} variant="outline" className="font-bold">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Neu
          </Button>
          <Button onClick={share} disabled={loading || busy || !data} variant="outline" className="font-bold">
            <Share2 className="h-4 w-4 mr-1" /> Teilen
          </Button>
          <Button onClick={download} disabled={loading || busy || !data} className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
            <Download className="h-4 w-4 mr-1" /> PNG
          </Button>
        </div>
      </div>
    </section>
  );
}
